import {
    ACTIONS,
    COMPONENTS,
    ELEMENTS,
    SERVICE_ADMIN_UPCOMING_EVENT_TEST_IDS,
    tid,
} from '@fiji/common/src/testing/testId';

const normalizeTestIdSegment = (value: string): string =>
    value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '') || 'unknown';

export const UPCOMING_EVENTS_DETAILS_TEST_IDS = {
    page: {
        root: 'ued_page_root',
        header: 'ued_page_header',
        summaryCard: 'ued_page_summary_card',
        summaryText: 'ued_page_summary_text',
        detailCard: 'ued_page_detail_card',
        detailText: 'ued_page_detail_text',
        noDataMessage: 'ued_page_no_data_message',
    },
    action: {
        acknowledgeButton: 'ued_action_acknowledge_btn',
        serviceRequestAcknowledgedButton: 'ued_action_service_request_ack_btn',
        serviceRequestUnacknowledgedButton: 'ued_action_service_request_unack_btn',
    },
    serviceBreakerContent: {
        container: 'ued_sb_content_container',
        leftColumn: 'ued_sb_content_left_col',
        middleColumn: 'ued_sb_content_mid_col',
        rightColumn: 'ued_sb_content_right_col',
    },
    descriptionCard: {
        root: 'ued_desc_card_root',
        descriptionText: 'ued_desc_card_text',
    },
    pulseScoreCard: {
        root: 'ued_score_card_root',
        scoreText: 'ued_score_card_text',
        learnMoreButton: 'ued_score_card_learn_more_btn',
    },
    scoreModal: {
        root: 'ued_score_modal_root',
        gradesRow: 'ued_score_modal_grades_row',
        weightedMethodText: 'ued_score_modal_weighted_method_text',
        weightsList: 'ued_score_modal_weights_list',
        deviceModeItem: 'ued_score_modal_device_mode_item',
        eventsAndAlarmsItem: 'ued_score_modal_events_and_alarms_item',
        deviceHealthDataItem: 'ued_score_modal_device_health_data_item',
        historicalDataItem: 'ued_score_modal_historical_data_item',
        configurationAgeItem: 'ued_score_modal_configuration_age_item',
        refreshedText: 'ued_score_modal_refreshed_text',
        devicesInNormalModeText: 'ued_score_modal_devices_in_normal_mode_text',
        notAllDevicesSupportedText: 'ued_score_modal_not_all_devices_supported_text',
        gradeBadge: (grade: string, discriminator?: string | number): string =>
            tid.upcomingEvents(
                COMPONENTS.MODAL,
                ELEMENTS.CHIP,
                undefined,
                `score-grade-${normalizeTestIdSegment(grade)}`,
                discriminator
            ),
        groupScoreText: tid.upcomingEvents(COMPONENTS.MODAL, ELEMENTS.TEXT, undefined, 'group-score-description'),
        weightRow: (label: string, discriminator?: string | number): string =>
            tid.upcomingEvents(COMPONENTS.MODAL, ELEMENTS.ROW, undefined, normalizeTestIdSegment(label), discriminator),
        weightLabel: (label: string, discriminator?: string | number): string =>
            tid.upcomingEvents(
                COMPONENTS.MODAL,
                ELEMENTS.TEXT,
                undefined,
                normalizeTestIdSegment(label),
                discriminator
            ),
        weightPoints: (label: string, discriminator?: string | number): string =>
            tid.upcomingEvents(
                COMPONENTS.MODAL,
                ELEMENTS.CELL,
                undefined,
                normalizeTestIdSegment(label),
                discriminator
            ),
        okButton: tid.upcomingEvents(COMPONENTS.MODAL, ELEMENTS.BTN, ACTIONS.CONFIRM, 'score'),
    },
    supportTechniciansCard: {
        root: 'ued_tech_card_root',
        listItem: 'ued_tech_card_list_item',
    },
    recommendationCard: {
        root: 'ued_reco_card_root',
        primaryText: 'ued_reco_card_primary_text',
        secondaryText: 'ued_reco_card_secondary_text',
    },
    evidenceCard: {
        root: 'ued_evidence_card_root',
        summaryText: 'ued_evidence_card_summary_text',
    },
    propertiesCard: {
        root: 'ued_properties_card_root',
        viewAllButton: 'ued_properties_card_view_all_btn',
    },
} as const;

export const UPCOMING_WEB_TEST_IDS = {
    capRul: {
        eventAvatar: 'cap-rul-event-avatar',
        eventNote: SERVICE_ADMIN_UPCOMING_EVENT_TEST_IDS.page.eventStatement,
        statusSelect: 'cap-rul-status-select',
        statusIndicator: 'cap-rul-status-indicator',
        statusText: 'cap-rul-status-text',
        acknowledgeButton: 'cap-rul-acknowledge-button',
        clearAlarmButton: 'cap-rul-clear-alarm-button',
        updateInstallationDateButton: 'cap-rul-update-installation-date-button',
        openCommentButton: 'cap-rul-open-comment-button',
        clearAlarmModal: 'cap-rul-clear-alarm-modal',
    },
    evidence: {
        expandLifeButton: 'cap-rul-evidence-expand-life-button',
        expandDegradeButton: 'cap-rul-evidence-expand-degrade-button',
    },
    updateService: {
        modal: 'cap-rul-update-service-modal',
        cancelButton: 'cap-rul-update-service-cancel-button',
        submitButton: 'cap-rul-update-service-submit-button',
        dateSelect: 'cap-rul-update-service-date-select',
    },
    batteryDegradation: {
        root: 'battery-degradation-root',
        eventNote: SERVICE_ADMIN_UPCOMING_EVENT_TEST_IDS.page.eventStatement,
        statusSelect: 'battery-degradation-status-select',
        openCommentButton: 'battery-degradation-open-comment-button',
        detailsCard: 'battery-degradation-details-card',
        detailsNotes: 'battery-degradation-details-notes',
        algorithmCard: 'battery-degradation-algorithm-card',
        algorithmImage: 'battery-degradation-algorithm-image',
        commentModal: 'battery-degradation-comment-modal',
    },
    properties: {
        root: 'upcoming-properties-card',
        list: 'upcoming-properties-list',
    },
    componentProperties: {
        root: 'upcoming-component-properties-card',
        list: 'upcoming-component-properties-list',
    },
    virtualLoads: {
        root: 'upcoming-virtual-loads-card',
    },
} as const;

export const getUpcomingStatusOptionTestId = (status: string): string =>
    `battery-degradation-status-option-${status.toLowerCase()}`;

export const getUpcomingCardActionTestId = (label: string): string =>
    `cap-rul-card-action-${label
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')}`;
